import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { fetchPastBishops } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Crown, 
  Award, 
  History, 
  Quote, 
  ArrowUpRight 
} from 'lucide-react';

const BASE_URL = `https://api.ibadansouthanglicandiocese.org.ng/`;


interface PastBishop {
  id: string;
  title: string;
  name: string;
  tenure: string;
  photo: string;
  qualifications: string;
  enthronement_date: number;
  endYear: number;
  bio: string;
  achievements?: string[];
}

const PastBishops = () => {
  const [pastBishops, setPastBishops] = useState<PastBishop[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadPastBishops = async () => {
      try {
        const response = await fetchPastBishops();
        setPastBishops(response.data.bishops);
      } catch (error) {
        console.error('Error fetching past bishops:', error);
        toast({ title: "Archival Record", description: "Displaying historical records from the registry." });
      } finally {
        setLoading(false);
      }
    };
    loadPastBishops();
  }, [toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fcfaf7] flex items-center justify-center">
        <div className="flex flex-col items-center">
          <History className="h-10 w-10 text-amber-600 animate-pulse mb-4" />
          <p className="text-slate-500 font-serif italic">Accessing Archives...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fcfaf7] py-24 px-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Header Section */}
        <div className="text-center mb-24">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-3 mb-4"
          >
            <div className="h-px w-12 bg-amber-500/30" />
            <span className="text-amber-600 uppercase tracking-[0.4em] text-xs font-bold">The Legacy</span>
            <div className="h-px w-12 bg-amber-500/30" />
          </motion.div>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-slate-900 mb-6">
            Past Bishops
          </h1>
          <p className="text-xl text-slate-500 max-w-2xl mx-auto font-serif italic">
            "Remember your leaders, who spoke the word of God to you. Consider the outcome of their way of life and imitate their faith."
          </p>
        </div>

        {/* Timeline Grid */}
        <div className="relative">
          {/* Central Timeline Line (Desktop) */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-200 hidden lg:block" />

          <div className="space-y-20">
            {pastBishops
              .sort((a, b) => b.enthronement_date - a.enthronement_date)
              .map((bishop, idx) => (
                <motion.div
                  key={bishop.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className={`flex flex-col lg:flex-row items-center gap-12 ${
                    idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                >
                  {/* Photo Container */}
                  <div className="w-full lg:w-1/2 flex justify-center">
                    <div className="relative group w-full max-w-md">
                      <div className="absolute inset-0 bg-slate-900 rounded-[3rem] rotate-3 transition-transform group-hover:rotate-0" />
                      <div className="relative aspect-[4/5] overflow-hidden rounded-[3rem] bg-slate-200 shadow-2xl border-4 border-white grayscale group-hover:grayscale-0 transition-all duration-700">
                        <img
                          src={bishop.photo.startsWith('http') ? bishop.photo : `${BASE_URL}${bishop.photo}`}
                          alt={bishop.name}
                          className="w-full h-full object-cover scale-110 group-hover:scale-100 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </div>
                    </div>
                  </div>

                  {/* Content Container */}
                  <div className="w-full lg:w-1/2 space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-amber-50 rounded-2xl">
                        <Crown className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-amber-600 uppercase tracking-widest">
                          Enthroned {bishop.enthronement_date}
                        </span>
                        <h2 className="text-3xl font-serif font-bold text-slate-900">
                          {bishop.title} {bishop.name}
                        </h2>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                       <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-[10px] font-bold uppercase">
                         {bishop.qualifications}
                       </span>
                    </div>

                    <p className="text-slate-600 font-serif leading-relaxed text-lg italic relative pl-8">
                       <Quote className="absolute left-0 top-0 h-6 w-6 text-slate-200" />
                       {bishop.bio}
                    </p>

                    {bishop.achievements && (
                      <div className="pt-6 border-t border-slate-200">
                        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2 mb-4">
                          <Award className="h-4 w-4 text-amber-500" />
                          Episcopal Landmarks
                        </h3>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {bishop.achievements.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-500">
                              <ArrowUpRight className="h-3 w-3 mt-1 text-amber-500 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
          </div>
        </div>

        {pastBishops.length === 0 && (
          <div className="text-center py-24">
            <History className="h-12 w-12 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400 font-serif">Registry archives are currently being digitized.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PastBishops;